import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { InputBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Components
import InfoPanel from './InfoPanel';
import InfoDialog from './InfoDialog';
import LineItem from './LineItem';

// Server
import { renameNetworkNode } from '../api';

// Constants
import { isNull } from '../constants';
import { T, FONT_MONO } from '../constants/tokens';
import {
  statusOf, describe, formatQuality, formatRssi, formatLatency, formatBattery, formatSeen,
  qualityText, batteryText, qualityFill
} from '../pages/network/format';

const useStyles = makeStyles({
  save: {
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    height: 36,
    padding: '0 14px',
    borderRadius: 6,
    border: '1px solid ' + T.hairline,
    background: T.elevated,
    color: T.ink,
    cursor: 'pointer',
    '&:hover:not(:disabled)': { background: T.surfaceSoft },
    '&:disabled': { color: T.mutedSoft, cursor: 'default' }
  }
});

const HEADER = (node) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontFamily: FONT_MONO, fontSize: 20, fontWeight: 500, lineHeight: '28px', color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {node.getNodeAddress()}
    </div>
    <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.muted }}>
      {describe(node)}
    </div>
  </div>
);

/**
 * The label field. Gateways and anchors have no other record to name them,
 * so a Super user names them here, and the name follows them to the Network
 * screen.
 */
function NameField({ node, onRenamed }) {
  const classes = useStyles();
  const [value, setValue] = useState(node.getName() ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setValue(node.getName() ?? '');
    setError(null);
  }, [node]);

  const dirty = value.trim() !== (node.getName() ?? '');

  const save = () => {
    setSaving(true);
    setError(null);
    renameNetworkNode(node.getNodeAddress(), value.trim())
      .then((updated) => onRenamed && onRenamed(updated))
      .catch(() => setError('Could not save the name.'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid ' + T.hairlineSoft }}>
      <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body, marginBottom: 8 }}>
        Name
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <InputBase
          value={value}
          placeholder={node.isGateway() ? 'e.g. GW north wing' : 'e.g. Room 118'}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && dirty && !saving) save();
          }}
          inputProps={{ 'aria-label': 'Name', maxLength: 255 }}
          style={{ flex: 1, height: 36, padding: '0 10px', fontSize: 14, border: '1px solid ' + T.hairline, borderRadius: 6, background: T.elevated }}
        />
        <button type='button' className={classes.save} onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {error && <div style={{ fontSize: 12, color: T.body, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

NameField.propTypes = {
  node: PropTypes.object.isRequired,
  onRenamed: PropTypes.func
};

const CHILDREN = (node, onRenamed) => {
  const status = statusOf(node);
  const hops = node.getHopCount();

  if (node.isGateway()) {
    return (
      <>
        <NameField node={node} onRenamed={onRenamed} />
        <LineItem description='Role' value='GW' />
        <LineItem description='Network id' value={node.networkAddress == null ? null : String(node.networkAddress)} mono />
        <LineItem description='Status' value={status.label} tone={status.text} />
        <LineItem description='Firmware' value={node.getAppVersion() && 'v' + node.getAppVersion()} mono />
        <LineItem description='Last seen' value={formatSeen(node)} mono last />
      </>
    );
  }

  return (
    <>
      <NameField node={node} onRenamed={onRenamed} />
      <LineItem description='Role' value={node.getRoleLabel()} />
      <LineItem description='Mesh status' value={status.label} tone={status.text} />
      <LineItem description='Signal quality' value={formatQuality(node)} mono tone={qualityText(node.getNextHopQuality())} />
      <LineItem description='Signal strength' value={formatRssi(node)} mono />
      <LineItem description='Next hop' value={node.getNextHop()} mono />
      <LineItem description='Hops to gateway' value={hops == null ? null : String(hops)} mono />
      <LineItem description='Gateway' value={node.getSinkAddress()} mono />
      <LineItem description='Latency' value={formatLatency(node)} mono />
      <LineItem description='Battery' value={formatBattery(node)} mono tone={batteryText(node)} />
      <LineItem description='Firmware' value={node.getAppVersion() && 'v' + node.getAppVersion()} mono />
      <LineItem description='Last seen' value={formatSeen(node)} mono last />
    </>
  );
};

export default function NetworkNodeInfoPanel({ node, handleClose, isMobile, onRenamed }) {
  const statusColor = node.isGateway() || node.getIsConnected() === false
    ? statusOf(node).dot
    : qualityFill(node.getNextHopQuality());

  return isMobile ? (
    <InfoDialog statusColor={statusColor} header={HEADER(node)} handleClose={handleClose} open={!isNull(node)}>
      {CHILDREN(node, onRenamed)}
    </InfoDialog>
  ) : (
    <InfoPanel statusColor={statusColor} header={HEADER(node)}>
      {CHILDREN(node, onRenamed)}
    </InfoPanel>
  );
}

NetworkNodeInfoPanel.propTypes = {
  node: PropTypes.object.isRequired,
  handleClose: PropTypes.func,
  isMobile: PropTypes.bool,
  onRenamed: PropTypes.func
};
